import React, { useState, useEffect } from 'react'
import { FaPalette, FaSave, FaUpload, FaUndo, FaEye } from 'react-icons/fa'
import settingsService from '../services/settingsService'
import { toast } from 'react-toastify'
import { useTheme } from '../context/ThemeContext'
import Loader from './Loader'

const ThemeSettings = () => {
  const { theme, refreshTheme } = useTheme()
  const [themeForm, setThemeForm] = useState({
    company_logo_url: '',
    theme_primary_color: '#3B82F6',
    theme_secondary_color: '#10B981',
    theme_accent_color: '#F59E0B',
    theme_text_color: '#1F2937',
    theme_background_color: '#FFFFFF',
    theme_navbar_style: 'light',
    theme_button_radius: 'rounded',
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [previewMode, setPreviewMode] = useState(false)

  useEffect(() => {
    loadThemeSettings()
  }, [])

  const loadThemeSettings = async () => {
    try {
      setLoading(true)
      const response = await settingsService.getPublicSettings()
      if (response.success && response.settings) {
        setThemeForm({
          company_logo_url: response.settings.company_logo_url || '',
          theme_primary_color: response.settings.theme_primary_color || '#3B82F6',
          theme_secondary_color: response.settings.theme_secondary_color || '#10B981',
          theme_accent_color: response.settings.theme_accent_color || '#F59E0B',
          theme_text_color: response.settings.theme_text_color || '#1F2937',
          theme_background_color: response.settings.theme_background_color || '#FFFFFF',
          theme_navbar_style: response.settings.theme_navbar_style || 'light',
          theme_button_radius: response.settings.theme_button_radius || 'rounded',
        })
      }
    } catch (error) {
      toast.error('Failed to load theme settings')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setThemeForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      // Update each setting individually
      const updates = Object.entries(themeForm).map(([key, value]) =>
        settingsService.updateSetting(key, value),
      )

      await Promise.all(updates)

      toast.success('Theme settings saved! Refreshing theme...')

      // Refresh the theme context
      setTimeout(() => {
        refreshTheme()
        window.location.reload() // Force reload to apply all changes
      }, 500)
    } catch (error) {
      toast.error('Failed to save theme settings')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    loadThemeSettings()
    toast.info('Theme settings reset to last saved values')
  }

  const applyPreview = () => {
    const root = document.documentElement
    root.style.setProperty('--color-primary', themeForm.theme_primary_color)
    root.style.setProperty('--color-secondary', themeForm.theme_secondary_color)
    root.style.setProperty('--color-accent', themeForm.theme_accent_color)
    setPreviewMode(true)
    toast.info('Preview applied - Save to make permanent')
  }

  if (loading) {
    return <Loader />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FaPalette className="text-blue-600" />
            Theme & Branding Settings
          </h2>
          <p className="text-gray-600 mt-1">Customize your website's appearance and branding</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
          >
            <FaUndo /> Reset
          </button>
          <button
            onClick={applyPreview}
            className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
          >
            <FaEye /> Preview
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            <FaSave /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {previewMode && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
          <p className="text-yellow-800">
            <strong>Preview Mode Active:</strong> Changes are visible but not saved. Click "Save
            Changes" to make them permanent.
          </p>
        </div>
      )}

      {/* Logo Section */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Company Logo</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Logo URL</label>
            <div className="flex gap-2">
              <input
                type="text"
                name="company_logo_url"
                value={themeForm.company_logo_url}
                onChange={handleInputChange}
                placeholder="https://example.com/logo.png"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition">
                <FaUpload /> Upload
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Recommended size: 200x60px (transparent PNG)
            </p>
          </div>

          {themeForm.company_logo_url && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-2">Logo Preview:</p>
              <div className="bg-white p-4 rounded border inline-block">
                <img
                  src={themeForm.company_logo_url}
                  alt="Company Logo"
                  className="h-16 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none'
                    e.target.nextSibling.style.display = 'block'
                  }}
                />
                <p className="text-red-500 text-sm" style={{ display: 'none' }}>
                  Failed to load image. Please check the URL.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Color Scheme */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Color Scheme</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Primary Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                name="theme_primary_color"
                value={themeForm.theme_primary_color}
                onChange={handleInputChange}
                className="h-12 w-20 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                name="theme_primary_color"
                value={themeForm.theme_primary_color}
                onChange={handleInputChange}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Main brand color (buttons, links)</p>
          </div>

          {/* Secondary Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                name="theme_secondary_color"
                value={themeForm.theme_secondary_color}
                onChange={handleInputChange}
                className="h-12 w-20 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                name="theme_secondary_color"
                value={themeForm.theme_secondary_color}
                onChange={handleInputChange}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Secondary highlights</p>
          </div>

          {/* Accent Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Accent Color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                name="theme_accent_color"
                value={themeForm.theme_accent_color}
                onChange={handleInputChange}
                className="h-12 w-20 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                name="theme_accent_color"
                value={themeForm.theme_accent_color}
                onChange={handleInputChange}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Special highlights</p>
          </div>

          {/* Text Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Text Color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                name="theme_text_color"
                value={themeForm.theme_text_color}
                onChange={handleInputChange}
                className="h-12 w-20 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                name="theme_text_color"
                value={themeForm.theme_text_color}
                onChange={handleInputChange}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Primary text color</p>
          </div>

          {/* Background Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Background Color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                name="theme_background_color"
                value={themeForm.theme_background_color}
                onChange={handleInputChange}
                className="h-12 w-20 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                name="theme_background_color"
                value={themeForm.theme_background_color}
                onChange={handleInputChange}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Page background</p>
          </div>
        </div>
      </div>

      {/* Style Options */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Style Options</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Navbar Style */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Navbar Style</label>
            <select
              name="theme_navbar_style"
              value={themeForm.theme_navbar_style}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="light">Light Background</option>
              <option value="dark">Dark Background</option>
            </select>
          </div>

          {/* Button Radius */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Button Style</label>
            <select
              name="theme_button_radius"
              value={themeForm.theme_button_radius}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="square">Square (No Radius)</option>
              <option value="rounded">Rounded (Medium)</option>
              <option value="pill">Pill (Full Rounded)</option>
            </select>
            <div className="flex gap-2 mt-3">
              <button
                className={`px-4 py-2 bg-blue-600 text-white text-sm ${
                  themeForm.theme_button_radius === 'square'
                    ? ''
                    : themeForm.theme_button_radius === 'rounded'
                      ? 'rounded-lg'
                      : 'rounded-full'
                }`}
              >
                Sample Button
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Color Preview */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Preview</h3>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div
              className="w-12 h-12 rounded"
              style={{ backgroundColor: themeForm.theme_primary_color }}
            ></div>
            <div>
              <p className="text-sm font-medium">Primary Color</p>
              <p className="text-xs text-gray-500">Buttons, links, primary actions</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-12 h-12 rounded"
              style={{ backgroundColor: themeForm.theme_secondary_color }}
            ></div>
            <div>
              <p className="text-sm font-medium">Secondary Color</p>
              <p className="text-xs text-gray-500">Secondary elements, badges</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-12 h-12 rounded"
              style={{ backgroundColor: themeForm.theme_accent_color }}
            ></div>
            <div>
              <p className="text-sm font-medium">Accent Color</p>
              <p className="text-xs text-gray-500">Highlights, notifications</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ThemeSettings
